import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { LpAllocationRow, LpAllocations } from '@/types/lpAllocation';
import {
  formatCurrency,
  formatPercentage,
  formatShares,
} from '@/utils/formatters';

import {
  draftAmount,
  draftTotal,
  isOverCommitment,
  remainingCommitment,
} from './lpAllocationValidators';
import type { EditorMode, LpAllocationEditor } from './useLpAllocationEditor';

function cardDescription(allocations: LpAllocations, mode: EditorMode) {
  if (mode === 'EDITING') {
    return 'How much of their commitment each LP puts into this investment.';
  }
  const shown = 'Only the LPs in this investment are shown.';
  return allocations.is_editable
    ? shown
    : `${shown} Locked once the investment is approved.`;
}

export function LpAllocationCard({
  editor,
  currency,
}: {
  editor: LpAllocationEditor;
  currency: string;
}) {
  const { allocations } = editor;
  if (!allocations || !editor.isTicked) return null;

  return (
    <Card data-testid="lp-allocation-section">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="text-base">LP allocation</CardTitle>
          <CardDescription>
            {cardDescription(allocations, editor.mode)}
          </CardDescription>
        </div>
        {allocations.is_editable && editor.isEditing && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={editor.isSaving}
              onClick={editor.cancel}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={editor.isSaving} onClick={editor.save}>
              {editor.isReplacing ? 'Saving...' : 'Save allocation'}
            </Button>
          </div>
        )}
        {allocations.is_editable && !editor.isEditing && (
          <Button size="sm" onClick={editor.startEditing}>
            Edit allocation
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editor.isOutOfStepWithInvestment && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              This allocation no longer adds up to the investment amount. Edit
              it before approving.
            </AlertDescription>
          </Alert>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>LP</TableHead>
              <TableHead className="text-right">Commitment</TableHead>
              <TableHead className="text-right">Other investments</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">% of investment</TableHead>
              {allocations.shares != null && (
                <TableHead className="text-right">Shares</TableHead>
              )}
              <TableHead className="text-right">Remaining commitment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {editor.rows.map((row) => (
              <LpAllocationTableRow
                key={row.investor_fund_relationship_id}
                row={row}
                amount={draftAmount(editor.draft, row)}
                investedAmount={allocations.invested_amount}
                shares={allocations.shares}
                currency={currency}
                isEditable={editor.isEditing}
                onAmountChange={(amount) =>
                  editor.setAmount(row.investor_fund_relationship_id, amount)
                }
              />
            ))}
            <TableRow className="font-medium">
              <TableCell>Total</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right">
                {formatCurrency(draftTotal(editor.draft), { currency })}
                <div className="text-xs font-normal text-muted-foreground">
                  of {formatCurrency(allocations.invested_amount, { currency })}
                </div>
              </TableCell>
              <TableCell />
              {allocations.shares != null && <TableCell />}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        {editor.errors.total && (
          <p className="mt-3 text-sm text-destructive">{editor.errors.total}</p>
        )}
        {editor.errors.overCommitment && (
          <p className="mt-3 text-sm text-destructive">
            {editor.errors.overCommitment}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function LpAllocationTableRow({
  row,
  amount,
  investedAmount,
  shares,
  currency,
  isEditable,
  onAmountChange,
}: {
  row: LpAllocationRow;
  amount: number;
  investedAmount: number;
  shares: number | null;
  currency: string;
  isEditable: boolean;
  onAmountChange: (amount: number | null) => void;
}) {
  const fraction = investedAmount ? amount / investedAmount : 0;
  const remaining = remainingCommitment(row, amount);

  return (
    <TableRow>
      <TableCell>{row.investor_name}</TableCell>
      <TableCell className="text-right">
        {formatCurrency(row.commitment_amount, { currency })}
      </TableCell>
      <TableCell className="text-right">
        {formatCurrency(row.allocated_to_other_investments, { currency })}
      </TableCell>
      <TableCell className="text-right">
        {isEditable ? (
          <CurrencyInput
            className="ml-auto w-40 text-right"
            currency={currency}
            value={amount}
            onChange={onAmountChange}
            aria-label={`Allocation for ${row.investor_name}`}
          />
        ) : (
          formatCurrency(amount, { currency })
        )}
      </TableCell>
      <TableCell className="text-right">{formatPercentage(fraction)}</TableCell>
      {shares != null && (
        <TableCell className="text-right">
          {formatShares(shares * fraction)}
        </TableCell>
      )}
      <TableCell
        className={
          isOverCommitment(row, amount)
            ? 'text-right text-destructive'
            : 'text-right'
        }
      >
        {formatCurrency(remaining, { currency })}
      </TableCell>
    </TableRow>
  );
}
